import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';
import { rolesHydratedAtom, rolesListParamsAtom } from '@/store';

export interface SearchFields {
  name: string;
  description: string;
}

export function useRolesFilters() {
  const [params, setParams] = useAtom(rolesListParamsAtom);
  const [, setHydrated] = useAtom(rolesHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    name: parseAsString.withDefault(''),
    description: parseAsString.withDefault(''),
    date: parseAsString.withDefault('')
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    name: queryParams.name,
    description: queryParams.description
  });

  // Search state
  const [searchTerm, setSearchTerm] = React.useState(queryParams.search);

  // Date filter state
  const [dateFilter, setDateFilter] = React.useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.name) filters.name = queryParams.name;
    if (queryParams.description) filters.description = queryParams.description;
    if (queryParams.date) filters.date = queryParams.date;

    // Only update if there's actually a change to avoid unnecessary API calls
    setParams((prev) => {
      const newParams = {
        ...prev,
        page: queryParams.page,
        filters: filters
      };

      // Check if anything actually changed
      if (prev.page === newParams.page &&
        JSON.stringify(prev.filters) === JSON.stringify(newParams.filters)) {
        return prev; // No change, return same object
      }

      return newParams;
    });

    // Mark hydrated after first effective sync
    setHydrated(true);
  }, [queryParams.page, queryParams.search, queryParams.name, queryParams.description, queryParams.date, setParams]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setSearchTerm(queryParams.search);
    setSearchFields({
      name: queryParams.name,
      description: queryParams.description
    });
  }, []); // Only run on mount

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: searchTerm || undefined }
    }));
    setQueryParams({ page: 1, search: searchTerm });
  }, [searchTerm, setParams, setQueryParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: undefined }
    }));
    setQueryParams({ page: 1, search: '' });
  }, [setParams, setQueryParams]);

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      name: searchFields.name || undefined,
      description: searchFields.description || undefined
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      name: searchFields.name,
      description: searchFields.description
    });

  }, [params.filters, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchTerm('');
    setSearchFields({ name: '', description: '' });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      search: '',
      name: '',
      description: ''
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return Boolean(searchTerm || searchFields.name || searchFields.description);
  }, [searchTerm, searchFields]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  const handleDateChange = React.useCallback((value: { start: Date | null; end: Date | null }) => {
    setDateFilter(value);

    let dateFilterValue = '';
    if (value.start && value.end) {
      // Format dates as YYYY-MM-DD
      const startDate = value.start.toISOString().split('T')[0];
      const endDate = value.end.toISOString().split('T')[0];
      dateFilterValue = `${startDate},${endDate}`;
    } else if (value.start) {
      dateFilterValue = value.start.toISOString().split('T')[0];
    }

    setQueryParams({ page: 1, date: dateFilterValue });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: {
        ...prev.filters,
        date: dateFilterValue || undefined
      }
    }));
  }, [setParams, setQueryParams]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    searchFields,
    setSearchFields,
    dateFilter,
    hasAnyFilter,

    // Actions
    handleSearch,
    handleClearSearch,
    applyFilters,
    clearFilters,
    handleDateChange,
    handlePageChange
  };
}
